import { Box, Paper } from "@mui/material";
import { GridPaginationModel } from "@mui/x-data-grid-premium";

import { ContextAnalysisPartsPanel } from "./ContextAnalysisPartsPanel";
import { DocumentAttachmentUsageTable } from "./DocumentAttachmentUsageTable";
import { PreludePane } from "./PreludePane";
import { RoundtripUsageTable } from "./RoundtripUsageTable";
import { prettyJson } from "./scoutContextAnalysisFormat";
import { rollupRoundtripsByTurn } from "./scoutContextAnalysisStats";
import { TurnRollupTable } from "./TurnRollupTable";
import {
  type PartRow,
  type SubTab,
  useScoutConversationContextAnalysis,
} from "./useScoutConversationContextAnalysis";

type Analysis = ReturnType<typeof useScoutConversationContextAnalysis>;

interface TabBodyProps {
  subTab: SubTab;
  analysis: Analysis;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  documentPaginationModel: GridPaginationModel;
  onDocumentPaginationModelChange: (model: GridPaginationModel) => void;
  onPartClick: (row: PartRow) => void;
}

export function ContextAnalysisTabPaper(props: TabBodyProps) {
  return (
    <Paper
      variant="outlined"
      sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      <ContextAnalysisTabBody {...props} />
    </Paper>
  );
}

function ContextAnalysisTabBody({
  subTab,
  analysis,
  paginationModel,
  onPaginationModelChange,
  documentPaginationModel,
  onDocumentPaginationModelChange,
  onPartClick,
}: TabBodyProps) {
  if (subTab === "parts") {
    return (
      <ContextAnalysisPartsPanel
        rows={analysis.partsResult?.items ?? []}
        loading={analysis.partsLoading}
        rowCount={analysis.stableRowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        onRowClick={onPartClick}
      />
    );
  }
  if (subTab === "roundtrips") {
    return (
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <RoundtripUsageTable rows={analysis.turns} />
      </Box>
    );
  }
  if (subTab === "turns") {
    return (
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <TurnRollupTable rows={rollupRoundtripsByTurn(analysis.turns)} />
      </Box>
    );
  }
  if (subTab === "documents") {
    return (
      <DocumentAttachmentUsageTable
        rows={analysis.documentsResult?.items ?? []}
        loading={analysis.documentsLoading}
        rowCount={analysis.stableDocumentRowCount}
        paginationModel={documentPaginationModel}
        onPaginationModelChange={onDocumentPaginationModelChange}
      />
    );
  }
  if (subTab === "system") {
    return <SystemPromptPane analysis={analysis} />;
  }
  return (
    <PreludePane
      text={
        analysis.prelude?.toolDefinitionsJson
          ? prettyJson(analysis.prelude.toolDefinitionsJson)
          : null
      }
      empty="No tool definitions recorded yet — see the System prompt tab note."
    />
  );
}

function SystemPromptPane({ analysis }: { analysis: Analysis }) {
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {analysis.prelude?.allowedSkillsJson ? (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: "action.hover",
            flexShrink: 0,
            fontFamily: "monospace",
            fontSize: 12,
          }}
        >
          Skill allow-list for turn {analysis.prelude.turnNumber ?? "?"}:{" "}
          {analysis.prelude.allowedSkillsJson}
        </Box>
      ) : null}
      <PreludePane
        text={analysis.prelude?.systemPromptText ?? null}
        empty="No system prompt recorded yet — turns persisted before the prelude rollout don't have content. Send a new message to populate."
      />
    </Box>
  );
}
